/**
 * Capability-based permissions mirroring backend accounts.permissions.
 *
 * Role strings stay on ``user.role``; call sites should check named capability
 * sets instead of ad-hoc ``role in (...)`` lists.
 */

import type { User, UserRoles } from "@/types/auth";

export const TRUE_ADMIN: readonly UserRoles[] = ["admin"];
export const ORG_MANAGER: readonly UserRoles[] = ["admin", "admin_support"];
export const FUNDER_STAFF: readonly UserRoles[] = ["admin", "admin_support", "underwriter"];
export const UNDERWRITING_ACTOR: readonly UserRoles[] = ["admin", "underwriter"];
export const STAFF_USER: readonly UserRoles[] = ["admin", "admin_support", "underwriter", "broker"];

/** Org fields that gate Merchant Tasks send for underwriters / brokers. */
export interface MerchantSendOrgSettings {
  underwriters_can_send_merchant_tasks?: boolean;
  brokers_can_send_merchant_tasks?: boolean;
}

export function hasCapability(
  user: Pick<User, "role"> | null | undefined,
  roles: readonly string[]
): boolean {
  return Boolean(user?.role && roles.includes(user.role));
}

export function isTrueAdmin(user: Pick<User, "role"> | null | undefined): boolean {
  return hasCapability(user, TRUE_ADMIN);
}

export function isOrgManager(user: Pick<User, "role"> | null | undefined): boolean {
  return hasCapability(user, ORG_MANAGER);
}

export function isFunderStaff(user: Pick<User, "role"> | null | undefined): boolean {
  return hasCapability(user, FUNDER_STAFF);
}

export function isUnderwritingActor(user: Pick<User, "role"> | null | undefined): boolean {
  return hasCapability(user, UNDERWRITING_ACTOR);
}

export function isStaffUser(user: Pick<User, "role"> | null | undefined): boolean {
  return hasCapability(user, STAFF_USER);
}

/** System admins manage what every organization shares; the backend enforces it. */
export function isSystemAdmin(user: Pick<User, "is_superuser"> | null | undefined): boolean {
  return Boolean(user?.is_superuser);
}

/** AI agent entry points; the backend decides (role, active, organization flag, user access). */
export function canUseAgent(user: Pick<User, "capabilities"> | null | undefined): boolean {
  return Boolean(user?.capabilities?.agent);
}

/** Settings' AI access section: the organization's admins, while it has the agent on. */
export function canManageAgentAccess(user: Pick<User, "capabilities"> | null | undefined): boolean {
  return Boolean(user?.capabilities?.manage_agent_access);
}

/** Fund/Decline from final underwriting: underwriting actors + admin support. */
export function canFundOrDeclineFromFinal(user: Pick<User, "role"> | null | undefined): boolean {
  return hasCapability(user, [...UNDERWRITING_ACTOR, "admin_support"]);
}

/** Whether ``user`` may start Merchant Tasks / send contracts for ``orgSettings``. */
export function canSendMerchantTasks(
  user: Pick<User, "role"> | null | undefined,
  orgSettings: MerchantSendOrgSettings | null | undefined
): boolean {
  if (!user?.role) return false;
  if (hasCapability(user, ORG_MANAGER)) return true;
  if (user.role === "underwriter") {
    return orgSettings?.underwriters_can_send_merchant_tasks !== false;
  }
  if (user.role === "broker") {
    return orgSettings?.brokers_can_send_merchant_tasks !== false;
  }
  return false;
}
